import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import type { TourStep } from '../../../types/onboarding';
import { TourStepRenderer } from '../TourStepRenderer';
import { TourSpotlight } from './TourSpotlight';
import { queryTourTarget, waitForTarget, isTourTargetVisible } from './waitForTarget';
import type { TourEngineCallbacks } from './TourEngine';
import { TOUR_DOM_EVENTS } from '../onboardingDomEvents';
import { scrollElementIntoSafeViewport } from '../../../../shared/onboarding/missionViewport';
import {
  classifyMissionTargetVisibility,
  isTourTargetMissionReady,
} from './missionTargetVisibility';

export type MuiTourEngineProps = {
  steps: TourStep[];
  index: number;
  run: boolean;
  spotlightPadding?: number;
  zIndex?: number;
  showSkipButton?: boolean;
  allowSkip?: boolean;
  callbacks: TourEngineCallbacks;
  onRequestSkip: () => void;
  onIndexChange: (index: number) => void;
  missionMode?: boolean;
  onRegisterRefresh?: (refresh: () => void) => void;
  onTargetVisibilityChange?: (state: 'ready' | 'missing' | 'occluded') => void;
  showMissionSpotlightNext?: boolean;
  onMissionSpotlightNext?: () => void;
};

export const MuiTourEngine: React.FC<MuiTourEngineProps> = ({
  steps,
  index,
  run,
  spotlightPadding = 8,
  zIndex,
  showSkipButton = true,
  allowSkip = true,
  callbacks,
  onRequestSkip,
  onIndexChange,
  missionMode = false,
  onRegisterRefresh,
  onTargetVisibilityChange,
  showMissionSpotlightNext = false,
  onMissionSpotlightNext,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const resolvedZIndex = zIndex ?? theme.zIndex.modal + 1200;
  const step = steps[index];
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [targetElement, setTargetElement] = useState<Element | null>(null);
  const viewedRef = useRef<Set<number>>(new Set());
  const beforeActionKeyRef = useRef<string | null>(null);
  const stepTargetKey = step
    ? `${index}:${step.stepName ?? ''}:${String(step.target ?? '')}`
    : '';

  const isCenterStep = useMemo(() => {
    if (!step) return true;
    const placement = (step.placement ?? '').toString();
    return placement === 'center' || !step.target;
  }, [step]);

  const { refs, floatingStyles } = useFloating({
    open: run && !!step,
    placement: isCenterStep ? 'bottom' : ((step?.placement as any) ?? 'bottom'),
    middleware: [offset(12), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const refreshTarget = useCallback(async () => {
    if (!run || !step) return;

    if (isCenterStep) {
      setTargetElement(document.body);
      setTargetRect(null);
      refs.setReference(null);
      return;
    }

    const target = step.target ?? 'body';

    if (step.beforeAction && beforeActionKeyRef.current !== stepTargetKey) {
      try {
        await step.beforeAction();
        beforeActionKeyRef.current = stepTargetKey;
      } catch {
        // non-blocking
      }
    }

    let element = queryTourTarget(target);
    if (!element || !isTourTargetVisible(element)) {
      element = await waitForTarget(target, { timeoutMs: 5000 });
    }

    if (!element || !isTourTargetVisible(element)) {
      setTargetElement(null);
      setTargetRect(null);
      refs.setReference(null);
      onTargetVisibilityChange?.('missing');
      callbacks.onTargetMissing(index, step);
      return;
    }

    const targetSelector = typeof target === 'string' ? target : '';
    const isNavDropdownItem =
      (targetSelector.includes('nav-browse-') &&
        !targetSelector.includes('nav-browse-button')) ||
      (targetSelector.includes('nav-about-us-') &&
        !targetSelector.includes('nav-about-us-button')) ||
      (targetSelector.includes('nav-user-') &&
        !targetSelector.includes('nav-user-menu'));

    if (!isNavDropdownItem && element instanceof HTMLElement) {
      if (missionMode) {
        scrollElementIntoSafeViewport(element);
        let visibility = classifyMissionTargetVisibility(element);
        let retries = 0;
        while (visibility === 'occluded' && retries < 2) {
          window.dispatchEvent(
            new CustomEvent(TOUR_DOM_EVENTS.MISSION_PANEL_COLLAPSE, {
              detail: { reason: 'occlusion' },
            }),
          );
          await new Promise((resolve) => window.setTimeout(resolve, 300));
          scrollElementIntoSafeViewport(element);
          visibility = classifyMissionTargetVisibility(element);
          retries += 1;
        }
        onTargetVisibilityChange?.(visibility);
        if (visibility === 'occluded') {
          setTargetElement(element);
          refs.setReference(element);
          setTargetRect(element.getBoundingClientRect());
          return;
        }
      } else {
        element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    } else if (missionMode) {
      onTargetVisibilityChange?.(
        isTourTargetMissionReady(element) ? 'ready' : 'occluded',
      );
    } else {
      onTargetVisibilityChange?.('ready');
    }
    setTargetElement(element);
    refs.setReference(element);
    setTargetRect(element.getBoundingClientRect());
  }, [callbacks, index, isCenterStep, missionMode, onTargetVisibilityChange, refs, run, step, stepTargetKey]);

  useEffect(() => {
    void refreshTarget();
  }, [refreshTarget]);

  useEffect(() => {
    onRegisterRefresh?.(() => {
      void refreshTarget();
    });
  }, [onRegisterRefresh, refreshTarget]);

  useEffect(() => {
    if (!run || !targetElement) return undefined;
    const update = () => setTargetRect(targetElement.getBoundingClientRect());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [run, targetElement]);

  useEffect(() => {
    if (!run || !step) return;
    if (viewedRef.current.has(index)) return;
    viewedRef.current.add(index);
    callbacks.onStepView(index, step);
  }, [callbacks, index, run, step]);

  useEffect(() => {
    if (run) {
      return;
    }
    viewedRef.current.clear();
    beforeActionKeyRef.current = null;
  }, [run]);

  useEffect(() => {
    if (!run) return undefined;
    const onPulse = (event: Event) => {
      const detail = (event as CustomEvent<{ anchor?: string }>).detail;
      const anchorFromGate =
        step?.metadata?.advanceGate &&
        typeof step.metadata.advanceGate === 'object' &&
        (step.metadata.advanceGate as { type?: string; anchor?: string }).type ===
          'click'
          ? (step.metadata.advanceGate as { anchor?: string }).anchor
          : undefined;
      const anchor = detail?.anchor ?? anchorFromGate;
      if (!anchor) {
        return;
      }
      const el =
        queryTourTarget(`[data-tour="${anchor}"]`) ??
        queryTourTarget(`[data-tour-stable="${anchor}"]`);
      if (!(el instanceof HTMLElement)) {
        return;
      }
      el.classList.add('mission-target-pulse');
      window.setTimeout(() => {
        el.classList.remove('mission-target-pulse');
      }, 2600);
    };
    window.addEventListener(TOUR_DOM_EVENTS.MISSION_TARGET_PULSE, onPulse);
    return () =>
      window.removeEventListener(TOUR_DOM_EVENTS.MISSION_TARGET_PULSE, onPulse);
  }, [run, step]);

  useEffect(() => {
    const stepName = step?.stepName;
    return () => {
      if (stepName === 'welcome_help') {
        window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.CLOSE_HELP_SPEED_DIAL));
      }
    };
  }, [index, run, step?.stepName]);

  useEffect(() => {
    if (!run) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && allowSkip) {
        event.preventDefault();
        onRequestSkip();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [allowSkip, onRequestSkip, run]);

  if (!run || !step) {
    return null;
  }

  const isLast = index >= steps.length - 1;
  const cardWidth = isMobile ? 'calc(100vw - 32px)' : Math.min(420, Math.max(380, window.innerWidth - 48));
  const titleId = `tour-step-title-${index}`;
  const advanceGateType = step.metadata?.advanceGate as { type?: string } | undefined;
  const allowTargetInteraction =
    advanceGateType?.type === 'click' ||
    (missionMode && advanceGateType?.type === 'route');

  const goNext = () => {
    if (missionMode) {
      return;
    }
    callbacks.onStepComplete(index, step);
    if (isLast) {
      callbacks.onComplete();
      return;
    }
    onIndexChange(index + 1);
  };

  const goBack = () => {
    if (index > 0) {
      onIndexChange(index - 1);
    }
  };

  const spotlightNextTop =
    targetRect != null
      ? Math.min(
          window.innerHeight - 44,
          targetRect.bottom + spotlightPadding + 10,
        )
      : null;
  const spotlightNextLeft =
    targetRect != null ? targetRect.left + targetRect.width / 2 : null;

  return (
    <>
      <TourSpotlight
        targetRect={isCenterStep ? null : targetRect}
        padding={spotlightPadding}
        zIndex={resolvedZIndex}
        allowTargetInteraction={allowTargetInteraction}
      />

      {missionMode &&
      showMissionSpotlightNext &&
      onMissionSpotlightNext &&
      !isCenterStep ? (
        targetRect ? (
          <Button
            variant="contained"
            size="small"
            onClick={onMissionSpotlightNext}
            data-tour-stable="mission-spotlight-next"
            sx={{
              position: 'fixed',
              top: spotlightNextTop,
              left: spotlightNextLeft,
              transform: 'translateX(-50%)',
              zIndex: resolvedZIndex + 2,
              pointerEvents: 'auto',
              boxShadow: 4,
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={onMissionSpotlightNext}
            data-tour-stable="mission-spotlight-next"
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: resolvedZIndex + 2,
              pointerEvents: 'auto',
              boxShadow: 4,
            }}
          >
            Next
          </Button>
        )
      ) : null}

      {!missionMode ? (
      <Box
        ref={refs.setFloating}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-tour-tooltip="true"
        sx={{
          ...(isCenterStep
            ? {
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }
            : floatingStyles),
          zIndex: resolvedZIndex + 1,
          width: cardWidth,
          maxWidth: cardWidth,
          maxHeight: 'calc(100vh - 100px)',
          overflow: 'auto',
        }}
      >
        <Paper elevation={8} sx={{ p: 2.5, borderRadius: 1 }}>
          <TourStepRenderer
            step={step}
            index={index}
            total={steps.length}
            titleId={titleId}
          />

          <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1}>
              {index > 0 && (
                <Button variant="outlined" onClick={goBack}>
                  Back
                </Button>
              )}
              {showSkipButton && allowSkip && (
                <Button color="inherit" onClick={onRequestSkip}>
                  Skip Tour
                </Button>
              )}
            </Stack>
            <Button variant="contained" onClick={goNext}>
              {isLast ? 'Finish' : 'Next'}
            </Button>
          </Stack>
        </Paper>
      </Box>
      ) : null}
    </>
  );
};

export default MuiTourEngine;
